import styled from 'styled-components';
import {
  NailTab,
  TabDescription,
  TabHeader,
} from 'src/components/common/NailTab';
import { media } from 'src/styles/designs';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
  EventHandler,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useTranslate } from 'react-t7e';
import { useDispatchAction } from 'vendor/hooks/useDispatchAction';
import { AppAction } from 'src/store/actions';
import { NeauviaItem } from 'src/models/directus';
import { makeTransition } from 'src/utils/motion/transitions';
import { useCallbackEffects } from 'src/hooks/useCallbackEffects';
import SwiperClass from 'swiper/types/swiper-class';
import { valueFromTargetDataset } from 'src/utils/eventHandlerHelpers';
import { dirRTL } from 'src/styles/theme';
import { useIsRTL } from 'src/hooks/useIsRTL';
import { useFastRefreshFixer } from 'src/hooks/dev/useFastRefreshFixer';
import { MaterialPageTab } from 'src/modules/materials/components/MaterialPage/MaterialPageTabs/types';

export interface MaterialPageTabsProps<T extends NeauviaItem = NeauviaItem> {
  tabs: MaterialPageTab<T>[];
  selectedTab: string;
  detailsIsVisible: boolean;
}

const StyledSwiper = styled(Swiper)`
  padding: 0 20px 0 0;
  ${dirRTL} {
    padding: 0 0 0 20px;
  }
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: visible;
  .swiper-slide {
    width: auto;
    min-width: min-content;
    white-space: nowrap;
    padding-right: 20px;
    ${dirRTL} {
      padding-right: 0;
      padding-left: 20px;
    }
  }

  ${media.w.greaterThanOrEqual.desktop} {
    .swiper-slide {
      width: auto;
      min-width: 205px;
    }

    ${NailTab} {
      grid-gap: 14px;
    }
  }
  ${media.w.lessThan.desktop} {
    margin-top: 10px;
  }
`;

const ScrollHere = styled.div`
  position: absolute;
  top: calc(-1 * var(--header-static-height) - 40px);
  left: 0;
`;

export const MaterialPageTabs = <T extends NeauviaItem>({
  tabs,
  selectedTab,
  detailsIsVisible,
}: MaterialPageTabsProps<T>) => {
  const { _ } = useTranslate();
  const isRTL = useIsRTL();
  const key = useFastRefreshFixer();

  const scrollHere = useRef<HTMLDivElement | null>(null);
  const tabsSwiperRef = useRef<SwiperClass>();
  const setSelectedTab = useDispatchAction(AppAction.detailsPageSetContentTab);

  useEffect(() => {
    tabsSwiperRef.current?.slideTo(
      tabs.findIndex(({ id }) => id === selectedTab),
    );
  }, [tabs, selectedTab]);

  const selectTab: EventHandler<SyntheticEvent<HTMLElement>> =
    useCallbackEffects(
      useCallback(valueFromTargetDataset('value')(setSelectedTab), [
        setSelectedTab,
      ]),
      useCallback(() => {
        if (!detailsIsVisible) {
          requestAnimationFrame(() => {
            scrollHere.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          });
        }
      }, [detailsIsVisible, selectedTab]),
    );

  return (
    <StyledSwiper
      key={`${isRTL}-${key}`}
      dir={isRTL ? 'rtl' : 'ltr'}
      onSwiper={(swiper: SwiperClass) => (tabsSwiperRef.current = swiper)}
      slidesPerView="auto"
      wrapperTag="ul"
    >
      <ScrollHere ref={scrollHere} />
      {tabs.map((tab, index) => (
        <SwiperSlide key={tab.id}>
          <NailTab
            {...makeTransition({
              fade: true,
              from: 'left',
              delay: 0 + index / 10,
            })}
            data-value={tab.id}
            onClick={selectTab}
            $active={selectedTab === tab.id}
          >
            <TabHeader>
              {_(tab.title ?? tab.id)} {!!tab.count && `(${tab.count})`}
            </TabHeader>
            <TabDescription>{_(tab.description)}</TabDescription>
          </NailTab>
        </SwiperSlide>
      ))}
    </StyledSwiper>
  );
};
